import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../lib/api';
import { useTheme, Icons } from '../lib/design-system';
import Spinner from '../components/ui/Spinner';
import { isNative } from '../lib/nativePhoto';

export default function TrainerRegister() {
  const { theme } = useTheme();
  const native = isNative();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!gdprConsent) {
      setError('Bitte stimme der Datenschutzerklärung und den Nutzungsbedingungen zu.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await authApi.register({
        email,
        password,
        firstName,
        lastName,
        role: 'TRAINER',
        schoolName: schoolName || undefined,
        gdprConsent: true,
      });
      const { user, token } = response.data;
      setUser(user, token);
      navigate('/trainer');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  // On iOS, trainer registration must happen on the web (Apple 3.1.1/2.1(b)):
  // subscription sign-up must not be initiated from the iOS app.
  if (native) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.accent} 100%)`,
        }}
      >
        <div
          className="w-full max-w-md p-8 rounded-3xl shadow-2xl text-center"
          style={{ backgroundColor: theme.surface }}
        >
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-neu-md"
            style={{ backgroundColor: theme.primaryLight }}
          >
            <Icons.Paw size={44} weight="duotone" style={{ color: theme.primary }} />
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ color: theme.text }}>
            Trainer-Konto erstellen
          </h1>
          <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
            Um sich als Trainer zu registrieren, besuchen Sie bitte{' '}
            <strong style={{ color: theme.text }}>mojo.sschuldt.de</strong>{' '}
            in Ihrem Browser. Sobald Ihr Konto erstellt ist, können Sie sich hier einloggen.
          </p>
          <Link
            to="/login"
            className="inline-block w-full py-3 px-4 rounded-xl text-sm font-semibold text-center"
            style={{ backgroundColor: theme.primary, color: '#ffffff' }}
          >
            Zum Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.accent} 100%)`,
      }}
    >
      <div
        className="w-full max-w-md p-8 md:p-10 rounded-3xl shadow-2xl"
        style={{ backgroundColor: theme.surface }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-neu-md"
            style={{ backgroundColor: theme.primaryLight }}
          >
            <Icons.Paw size={44} weight="duotone" style={{ color: theme.primary }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: theme.text }}>
            Mojo's Class
          </h1>
          <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
            Als Trainer registrieren
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Vorname
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input-neu"
              placeholder="Max"
              required
              autoComplete="given-name"
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Nachname
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input-neu"
              placeholder="Mustermann"
              required
              autoComplete="family-name"
            />
          </div>

          {/* School Name */}
          <div>
            <label htmlFor="schoolName" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Name deiner Hundeschule <span style={{ color: theme.textMuted }}>(optional)</span>
            </label>
            <input
              id="schoolName"
              type="text"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              className="input-neu"
              placeholder="z.B. Wuffelschule Musterstadt"
              autoComplete="organization"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              E-Mail
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: theme.textMuted }}>
                <Icons.Envelope size={20} />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-neu pl-12"
                placeholder="deine@email.de"
                required
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
              Passwort
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: theme.textMuted }}>
                <Icons.Lock size={20} />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-neu pl-12 pr-12"
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
                tabIndex={-1}
              >
                {showPassword ? <Icons.Hide size={20} /> : <Icons.View size={20} />}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
              Min. 8 Zeichen, Groß- und Kleinbuchstaben, eine Zahl
            </p>
          </div>

          {/* GDPR Consent */}
          <div className="flex items-start gap-3">
            <input
              id="gdprConsent"
              type="checkbox"
              checked={gdprConsent}
              onChange={(e) => setGdprConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded"
              style={{ accentColor: theme.primary }}
            />
            <label htmlFor="gdprConsent" className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
              Ich habe die{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: theme.primary }}>
                Datenschutzerklärung
              </a>{' '}
              und die{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: theme.primary }}>
                Nutzungsbedingungen
              </a>{' '}
              gelesen und stimme ihnen zu. *
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div
              role="alert"
              className="p-4 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: `${theme.error}15`, color: theme.error }}
            >
              <Icons.Warning size={20} weight="fill" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" />
                <span>Konto erstellen...</span>
              </>
            ) : (
              <span>Trainer-Konto erstellen</span>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <div>
            <Link to="/login" className="text-sm" style={{ color: theme.textMuted }}>
              Bereits registriert? Anmelden
            </Link>
          </div>
          <div>
            <Link to="/register" className="text-sm" style={{ color: theme.textMuted }}>
              Als Hundebesitzer registrieren
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
